// Two-to-three-sentence AI read under a dashboard table, with a one-click
// follow-up into the Guru dock that carries the same card context.
//
// Fetches only when the card is actually on screen (`active` and visible)
// and the rows are present; re-fetches when the rows change. The server
// caches per (client, card, data) for six hours, so re-opening a section
// is free.
import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Paper, Skeleton, Stack, Typography } from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';

import { fetchDailyPulseCardInsight } from '@src/api/actions/dashboard/dailyPulse';
import AskGuruButton from '@src/components/agent-chat/AskGuruButton';

export default function CardInsight({ clientId, parentCompanyId, card, scope, rows, active = true, sx }) {
  const [insight, setInsight] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const hasSubject = Boolean(active && (clientId || parentCompanyId) && card && rows && rows.length > 0);

  // Collapsed dashboard sections stay mounted with their rows populated, so
  // an eager fetch meant up to six Sonnet calls per dashboard load for
  // tables nobody opened. A 1px sentinel sits in the DOM until there is a
  // read to show; inside a collapsed Accordion it is clipped to nothing and
  // the observer stays quiet until the section opens.
  const sentinelRef = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (visible || !hasSubject) return undefined;
    const el = sentinelRef.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry && entry.isIntersecting && entry.boundingClientRect.height > 0) setVisible(true);
      },
      { threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [visible, hasSubject]);

  // Only the parts of a row the server reads change the key, so a re-render
  // with the same numbers doesn't refetch.
  const rowsKey = useMemo(() => {
    try {
      return JSON.stringify((rows || []).slice(0, 40));
    } catch (e) {
      return String((rows || []).length);
    }
  }, [rows]);

  useEffect(() => {
    if (!visible || !hasSubject) return undefined;
    let alive = true;
    setLoading(true);
    setFailed(false);
    // A brand, or a parent company's combined view (all brands).
    const subject = clientId ? { client_id: clientId } : { parent_company_id: parentCompanyId };
    fetchDailyPulseCardInsight({ ...subject, card, scope: scope || {}, rows: rows.slice(0, 40) })
      .then((res) => {
        if (!alive) return;
        setInsight(res?.data?.insight || null);
        setFailed(!res?.data?.insight);
      })
      .catch(() => { if (alive) setFailed(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // rowsKey stands in for rows; scope is stable per section.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, hasSubject, clientId, parentCompanyId, card, rowsKey]);

  if (!hasSubject) return null;
  if (!visible || (!loading && (failed || !insight))) {
    return <Box ref={sentinelRef} sx={{ height: '1px' }} />;
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        mt: 1.5,
        p: { xs: 1.5, md: 2 },
        borderColor: 'primary.light',
        bgcolor: 'rgba(79, 70, 229, 0.04)',
        ...sx,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <SmartToyIcon color="primary" sx={{ fontSize: 20, mt: 0.25 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.4 }}>
            GURU'S READ
          </Typography>
          {loading ? (
            <Box sx={{ mt: 0.5 }}>
              <Skeleton width="92%" />
              <Skeleton width="70%" />
            </Box>
          ) : (
            <Typography variant="body2" sx={{ mt: 0.25 }}>{insight}</Typography>
          )}
        </Box>
        {!loading && insight ? (
          <AskGuruButton
            variant="text"
            label="Ask a follow-up"
            clientId={clientId}
            parentCompanyId={parentCompanyId}
            card={card}
            scope={scope}
            prompt={`About my ${card}${scope?.period ? ` (${scope.period})` : ''}: "${insight}" — what's behind this, and what would you look at next?`}
            sx={{ whiteSpace: 'nowrap', alignSelf: 'center' }}
          />
        ) : null}
      </Stack>
    </Paper>
  );
}

CardInsight.propTypes = {
  clientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  parentCompanyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  card: PropTypes.string.isRequired,
  scope: PropTypes.object,
  rows: PropTypes.array,
  active: PropTypes.bool,
  sx: PropTypes.object,
};
